import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Play, Clock, Eye } from 'lucide-react';

const videos = [
  {
    id: 1,
    title: "Understanding AI in Healthcare",
    description: "Learn how artificial intelligence is transforming modern medicine and patient care.",
    thumbnail: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=400&fit=crop",
    duration: "12:45",
    views: "15.2K",
    youtubeId: "dQw4w9WgXcQ"
  },
  {
    id: 2,
    title: "Mental Health Tips for Daily Life",
    description: "Expert advice on maintaining mental wellness in today's fast-paced world.",
    thumbnail: "https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=600&h=400&fit=crop",
    duration: "8:30",
    views: "23.5K",
    youtubeId: "dQw4w9WgXcQ"
  },
  {
    id: 3,
    title: "Nutrition Basics Explained",
    description: "A comprehensive guide to balanced nutrition and healthy eating habits.",
    thumbnail: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600&h=400&fit=crop",
    duration: "15:20",
    views: "31.8K",
    youtubeId: "dQw4w9WgXcQ"
  },
  {
    id: 4,
    title: "Exercise Routines for Beginners",
    description: "Start your fitness journey with these simple and effective exercises.",
    thumbnail: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=400&fit=crop",
    duration: "20:15",
    views: "45.3K",
    youtubeId: "dQw4w9WgXcQ"
  },
  {
    id: 5,
    title: "Sleep Hygiene: Better Rest Tonight",
    description: "Discover the secrets to improving your sleep quality naturally.",
    thumbnail: "https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?w=600&h=400&fit=crop",
    duration: "10:45",
    views: "18.7K",
    youtubeId: "dQw4w9WgXcQ"
  },
  {
    id: 6,
    title: "Heart Health Awareness",
    description: "Essential information about maintaining a healthy heart at any age.",
    thumbnail: "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=600&h=400&fit=crop",
    duration: "14:30",
    views: "27.9K",
    youtubeId: "dQw4w9WgXcQ"
  }
];

const VideosPage = () => {
  const [selectedVideo, setSelectedVideo] = React.useState(null);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-[#f8fdf8] to-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-gray-700 text-sm font-medium border border-gray-200 mb-6">
              <span className="w-2 h-2 bg-[#166534] rounded-full" />
              Educational Content
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Health Videos
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Watch expert health videos covering wellness, nutrition, fitness, and mental health topics.
            </p>
          </div>
        </section>

        {/* Video Modal */}
        {selectedVideo && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedVideo(null)}
          >
            <div 
              className="bg-white rounded-2xl overflow-hidden max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${selectedVideo.youtubeId}?autoplay=1`}
                  title={selectedVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedVideo.title}</h3>
                <p className="text-gray-600">{selectedVideo.description}</p>
              </div>
            </div>
          </div>
        )}

        {/* Videos Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video) => (
                <div
                  key={video.id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 cursor-pointer"
                  onClick={() => setSelectedVideo(video)}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                        <Play className="w-8 h-8 text-[#166534] ml-1" fill="currentColor" />
                      </div>
                    </div>
                    <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      {video.duration}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#166534] transition-colors">
                      {video.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{video.description}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {video.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {video.views} views
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default VideosPage;
